import { useState, type ReactElement } from "react";
import { Button, Col, Container, Form, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { Trans, useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { ProjectSlice, selectProject, updateGammaApi } from "../features/projects/projectSlice";
import { SXEditField } from "@common/components/ui-components/SXEditField";
import { SXDropdown, type NGDropdownListItem } from "@common/components/ui-components/SXDropdown";
import { MaterialSettingsModal } from "./modals/MaterialSettingsModal";
import { AlertMessage } from "./ui-components/SXAlert/SXAlertMessage";
import { ng } from "@common/ngds";

interface MaterialSettingsProps {
  selectedGroup?: "Concrete" | "Steel" | "Timber";
}

const CREEP_LIMITS = { min: 0, max: 10 };
const SHRINKAGE_LIMITS = { min: 0, max: 1 };
const clampValue = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

// Every concrete setting on the row is a Eurocode quantity whose label alone does not say which
// clause it feeds, so each carries its `description` from the translation file as a hover
// explanation - already written and translated for all four languages, so there is nothing to
// keep in sync here. Only the header text is the trigger: arming the whole column would pop a
// tooltip over the field the moment you reached for it, and cover the value being typed.
// Upwards, not downwards: both dropdowns on the second row open with dropDirection="down", and a
// tooltip below the header would sit on top of the menu that just opened under the cursor.
const withInfoTooltip = (id: string, text: string, column: ReactElement) => (
  <OverlayTrigger
    placement="top"
    delay={{ show: 400, hide: 200 }}
    overlay={
      <Tooltip id={id} className="sx-tooltip-wide">
        {text}
      </Tooltip>
    }
  >
    {column}
  </OverlayTrigger>
);

// const settingsButtonLabels: Record<"Concrete" | "Steel" | "Timber", string> = {
//   Concrete: "Concrete settings",
//   Steel: "Steel settings",
//   Timber: "Timber settings",
// };

export const MaterialSettings = ({ selectedGroup = "Concrete" }: MaterialSettingsProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const selProject = useAppSelector(selectProject);
  const [showSettingsModal, setShowSettingsModal] = useState<boolean>(false);

  const exposureClassOptions: Array<NGDropdownListItem<string>> = [
    { id: "EXPOSURE_CLASS_X0", name: "X0" },
    { id: "EXPOSURE_CLASS_XC1", name: "XC1" },
    { id: "EXPOSURE_CLASS_XC2", name: "XC2" },
    { id: "EXPOSURE_CLASS_XC3", name: "XC3" },
    { id: "EXPOSURE_CLASS_XC4", name: "XC4" },
    { id: "EXPOSURE_CLASS_XD1", name: "XD1" },
    { id: "EXPOSURE_CLASS_XD2", name: "XD2" },
    { id: "EXPOSURE_CLASS_XD3", name: "XD3" },
    { id: "EXPOSURE_CLASS_XS1", name: "XS1" },
    { id: "EXPOSURE_CLASS_XS2", name: "XS2" },
    { id: "EXPOSURE_CLASS_XS3", name: "XS3" },
    { id: "EXPOSURE_CLASS_XA1", name: "XA1" },
    { id: "EXPOSURE_CLASS_XA2", name: "XA2" },
    { id: "EXPOSURE_CLASS_XA3", name: "XA3" },
  ];

  // L20 is only defined in the Swedish national annex. Every other annex has L50 and L100 only -
  // see sscCGeneralDataEUG1::GetNumOfLifeClasses() in WS_PRE_Stress.
  const isSwedishAnnex =
    ng.eurocode.Annex_FromObject(selProject?.data?.data?.input?.ec?.nationalAnnex) ===
    ng.eurocode.Annex.ANNEX_SWEDEN;

  const lifeCategoryOptions: Array<NGDropdownListItem<string>> = [
    ...(isSwedishAnnex ? [{ id: "LIFE_CATEGORY_L20", name: "L20" }] : []),
    { id: "LIFE_CATEGORY_L50", name: "L50" },
    { id: "LIFE_CATEGORY_L100", name: "L100" },
  ];

  const serviceClassOptions: Array<NGDropdownListItem<string>> = [
    { id: "SERVICE_CLASS_1", name: "Service class 1" },
    { id: "SERVICE_CLASS_2", name: "Service class 2" },
    { id: "SERVICE_CLASS_3", name: "Service class 3" },
  ];

  const updateStageRcSettings = (
    updater: (stageRc: Record<string, unknown>, stageRcSpecific: Record<string, unknown>) => void,
  ) => {
    if (!selProject) {
      return;
    }

    const nextProject = structuredClone(selProject);
    const structure = nextProject?.data?.data?.input?.structures?.[0] as Record<string, unknown> | undefined;
    if (!structure) {
      return;
    }

    if (!Array.isArray(structure.stages) || structure.stages.length === 0) {
      structure.stages = [{}];
    }
    const stages = structure.stages as unknown[];

    const stage0 =
      stages[0] && typeof stages[0] === "object"
        ? (stages[0] as Record<string, unknown>)
        : ({} as Record<string, unknown>);
    stages[0] = stage0;

    if (!Array.isArray(stage0.elems) || stage0.elems.length === 0) {
      stage0.elems = [{}];
    }
    const elems = stage0.elems as unknown[];

    const elem0 =
      elems[0] && typeof elems[0] === "object"
        ? (elems[0] as Record<string, unknown>)
        : ({} as Record<string, unknown>);
    elems[0] = elem0;

    if (!elem0.rc || typeof elem0.rc !== "object") {
      elem0.rc = {};
    }
    const stageRc = elem0.rc as Record<string, unknown>;

    if (!Array.isArray(stageRc.rcSpecifics) || stageRc.rcSpecifics.length === 0) {
      stageRc.rcSpecifics = [{}];
    }
    const rcSpecifics = stageRc.rcSpecifics as unknown[];
    const stageRcSpecific =
      rcSpecifics[0] && typeof rcSpecifics[0] === "object"
        ? (rcSpecifics[0] as Record<string, unknown>)
        : ({} as Record<string, unknown>);
    rcSpecifics[0] = stageRcSpecific;

    updater(stageRc, stageRcSpecific);
    dispatch(ProjectSlice.actions.applyProjectEdit(nextProject));
  };

  // Element specific rc design settings belong in the `column` member of the `element_type` oneof, never
  // in `beam`: the RC engine tests `has_beam()` first and would then ignore all of the column settings.
  const updateElementRcColumnSettings = (updater: (column: Record<string, unknown>) => void) => {
    if (!selProject) {
      return;
    }

    const nextProject = structuredClone(selProject);
    const element = nextProject?.data?.data?.input?.structures?.[0]?.elements?.[0] as
      | Record<string, unknown>
      | undefined;
    if (!element) {
      return;
    }

    element.designSettings ??= {};
    const designSettings = element.designSettings as Record<string, unknown>;

    if (!designSettings.rc || typeof designSettings.rc !== "object") {
      designSettings.rc = {};
    }
    const rc = designSettings.rc as Record<string, unknown>;

    if (!rc.column || typeof rc.column !== "object") {
      rc.column = {};
    }
    const column = rc.column as Record<string, unknown>;

    updater(column);
    dispatch(ProjectSlice.actions.applyProjectEdit(nextProject));
  };

  const updateElementTimberSettings = (
    updater: (timber: Record<string, unknown>, timberBeam: Record<string, unknown>) => void,
  ) => {
    if (!selProject) {
      return;
    }

    const nextProject = structuredClone(selProject);
    const element = nextProject?.data?.data?.input?.structures?.[0]?.elements?.[0] as
      | Record<string, unknown>
      | undefined;
    if (!element) {
      return;
    }

    element.designSettings ??= {};
    const designSettings = element.designSettings as Record<string, unknown>;

    if (!designSettings.timber || typeof designSettings.timber !== "object") {
      designSettings.timber = {};
    }
    const timber = designSettings.timber as Record<string, unknown>;

    if (!timber.beam || typeof timber.beam !== "object") {
      timber.beam = {};
    }
    const timberBeam = timber.beam as Record<string, unknown>;

    updater(timber, timberBeam);
    dispatch(ProjectSlice.actions.applyProjectEdit(nextProject));
  };

  const updateClampedStageRcSpecificValue = (
    field: "creep" | "effectiveCreep" | "shrinkage",
    value: number,
    label: string,
    limits: { min: number; max: number },
    unit?: string,
  ) => {
    const sanitizedValue = Number.isFinite(value) ? value : 0;
    const clampedValue = clampValue(sanitizedValue, limits.min, limits.max);

    if (clampedValue !== sanitizedValue) {
      const unitSuffix = unit ? ` ${unit}` : "";
      AlertMessage(
        "warning",
        t("warning_.label"),
        label,
        t("material_settings_clamped_range_warning_.label", {
          field: label,
          min: limits.min,
          max: limits.max,
          unit: unitSuffix,
        }),
      );
    }

    updateStageRcSettings((_stageRc, specific) => {
      specific[field] = clampedValue;
    });
  };

  const stageRc = selProject?.data?.data?.input?.structures?.[0]?.stages?.[0]?.elems?.[0]?.rc;
  const stageRcSpecific = stageRc?.rcSpecifics?.[0];

  const creep = Number(stageRcSpecific?.creep ?? 0);
  const effectiveCreep = Number(stageRcSpecific?.effectiveCreep ?? 0);
  const shrinkagePerMille = Number(stageRcSpecific?.shrinkage ?? 0);
  const considerMinReinf = Boolean(
    selProject?.data?.data?.input?.structures?.[0]?.elements?.[0]?.designSettings?.rc?.column?.useMinReinf,
  );

  const exposureClassId = String(stageRcSpecific?.exposureClass ?? "EXPOSURE_CLASS_XC1");
  // Show what RC-Design computes with. A project with no life category (older projects, or one saved as
  // UNSPECIFIED) is calculated as L100, with the warning warn_life_category_unspecified_
  // (ms-structural/WS_PRE_Stress#252), so it shows L100 rather than the L50 a new project starts with.
  // A project saved under the Swedish annex can still carry L20 after the annex is changed; it is not
  // an option here, so it falls back to L50 - which is what RC-Design maps it to for a non-Swedish
  // annex in Gen1.
  const storedLifeCategory = stageRc?.lifeCategory;
  const lifeCategoryId =
    storedLifeCategory == null || String(storedLifeCategory) === "LIFE_CATEGORY_UNSPECIFIED"
      ? "LIFE_CATEGORY_L100"
      : String(storedLifeCategory);

  const timberSettings = selProject?.data?.data?.input?.structures?.[0]?.elements?.[0]?.designSettings?.timber as
    | {
        serviceclass?: string;
        kdef?: number;
        kcr?: number;
        beam?: { ksys?: number };
      }
    | undefined;

  const serviceClassId = String(timberSettings?.serviceclass ?? "SERVICE_CLASS_1");
  const kdef = Number(timberSettings?.kdef ?? 0.8);
  const kcr = Number(timberSettings?.kcr ?? 0.67);
  const ksys = Number(timberSettings?.beam?.ksys ?? 1);
  const exposureClass = exposureClassOptions.find((option) => option.id === exposureClassId) ?? exposureClassOptions[1];
  const lifeCategory =
    lifeCategoryOptions.find((option) => option.id === lifeCategoryId) ??
    lifeCategoryOptions.find((option) => option.id === "LIFE_CATEGORY_L50")!;
  const serviceClass = serviceClassOptions.find((option) => option.id === serviceClassId) ?? serviceClassOptions[0];

  return (
    <Container id="sx-table-container" className="ps-3 px-3" style={{ marginTop: 20 }}>
      <Row className="align-items-center">
        {selectedGroup === "Concrete" && (
          <Col xs="auto">
            <Form.Check
              type="switch"
              className="sx-switch"
              label={t("use_min_reinforcement_.label")}
              id="sx-auto-buckling-length-switch"
              checked={considerMinReinf}
              onChange={(e) => {
                const checked = e.target.checked;
                updateElementRcColumnSettings((column) => {
                  column.useMinReinf = checked;
                });
              }}
            />
          </Col>
        )}
        <Col className="d-flex justify-content-end">
          <Button className="secondary-btn" onClick={() => setShowSettingsModal(true)}>
            {/* {settingsButtonLabels[selectedGroup]} */}
            {t("advanced_settings_.label")}
          </Button>
        </Col>
      </Row>

      {selectedGroup === "Concrete" && (
        <>
          <Row className="align-items-center gy-2" style={{ marginTop: 16 }}>
            <Col xs={12} md={6} lg={4}>
              <Row className="container-row">
                <Col className="editfield-label">
                  {withInfoTooltip(
                    "sx-creep-tooltip",
                    t("creep_.description"),
                    <span>
                      {t("creep_.label", "Creep")},{" "}
                      <span className="text-nowrap">φ ({t("sls_.label", "SLS")}) [-]</span>
                    </span>,
                  )}
                </Col>
                <Col xs={12} className="ps-0" style={{ height: 32 }}>
                  <SXEditField
                    type="number"
                    val={creep}
                    textAlign="left"
                    fullWidth
                    disableNegativeNumber
                    onUpdate={(value) => {
                      updateClampedStageRcSpecificValue(
                        "creep",
                        Number(value),
                        t("creep_.label", "Creep"),
                        CREEP_LIMITS,
                      );
                    }}
                  />
                </Col>
              </Row>
            </Col>

            <Col xs={12} md={6} lg={4}>
              <Row className="container-row">
                <Col className="editfield-label">
                  {withInfoTooltip(
                    "sx-effective-creep-tooltip",
                    t("effective_creep_.description"),
                    <span>
                      {t("creep_.label", "Creep")},{" "}
                      <span className="text-nowrap">
                        φ<sub>eff</sub> ({t("uls_.label", "ULS")}) [-]
                      </span>
                    </span>,
                  )}
                </Col>
                <Col xs={12} className="ps-0" style={{ height: 32 }}>
                  <SXEditField
                    type="number"
                    val={effectiveCreep}
                    textAlign="left"
                    fullWidth
                    disableNegativeNumber
                    onUpdate={(value) => {
                      updateClampedStageRcSpecificValue(
                        "effectiveCreep",
                        Number(value),
                        t("effective_creep_.label", "Effective creep"),
                        CREEP_LIMITS,
                      );
                    }}
                  />
                </Col>
              </Row>
            </Col>

            <Col xs={12} md={6} lg={4}>
              <Row className="container-row">
                <Col className="editfield-label">
                  {withInfoTooltip(
                    "sx-shrinkage-tooltip",
                    t("shrinkage_.description"),
                    <span>{t("shrinkage_.label", "Shrinkage")} [‰]</span>,
                  )}
                </Col>
                <Col xs={12} className="ps-0" style={{ height: 32 }}>
                  <SXEditField
                    type="number"
                    val={shrinkagePerMille}
                    textAlign="left"
                    fullWidth
                    disableNegativeNumber
                    onUpdate={(value) => {
                      updateClampedStageRcSpecificValue(
                        "shrinkage",
                        Number(value),
                        t("shrinkage_.label", "Shrinkage"),
                        SHRINKAGE_LIMITS,
                        "‰",
                      );
                    }}
                  />
                </Col>
              </Row>
            </Col>
          </Row>

          <Row className="align-items-center gy-2">
            <Col xs={12} md={6} lg={4}>
              <Row className="container-row">
                <Col className="editfield-label">
                  {withInfoTooltip(
                    "sx-exposure-class-tooltip",
                    t("exposure_class_.description"),
                    <span>{t("exposure_class_.label", "Exposure class")}</span>,
                  )}
                </Col>
                <Col xs={12} className="ps-0" style={{ height: 32 }}>
                  <SXDropdown
                    name="exposure_class"
                    id="exposure-class-select"
                    list={exposureClassOptions}
                    val={exposureClass}
                    dropDirection="down"
                    onUpdate={(value) => {
                      if (!value) {
                        return;
                      }
                      updateStageRcSettings((_stageRc, specific) => {
                        specific.exposureClass = value.id;
                      });
                    }}
                    notSelectedName="Select exposure class"
                    localisation={false}
                    rawNames={true}
                    lang=""
                  />
                </Col>
              </Row>
            </Col>

            <Col xs={12} md={6} lg={4}>
              <Row className="container-row">
                <Col className="editfield-label">
                  {withInfoTooltip(
                    "sx-life-category-tooltip",
                    t("life_category_.description"),
                    <span>{t("life_category_.label", "Life category")}</span>,
                  )}
                </Col>
                <Col xs={12} className="px-0" style={{ height: 32 }}>
                  <SXDropdown
                    name="life_category"
                    id="life-category-select"
                    list={lifeCategoryOptions}
                    val={lifeCategory}
                    dropDirection="down"
                    onUpdate={(value) => {
                      if (!value) {
                        return;
                      }
                      updateStageRcSettings((stageRcData) => {
                        stageRcData.lifeCategory = value.id;
                      });
                    }}
                    notSelectedName="Select life category"
                    localisation={false}
                    rawNames={true}
                    lang=""
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        </>
      )}

      {selectedGroup === "Timber" && (
        <Row className="container-row align-items-center" style={{ marginTop: 8 }}>
          <Col xs={12} md={6} lg={3}>
            <Row className="container-row">
              <Col className="editfield-label">
                <span>{t("service_class_.label", "Service class")}</span>
              </Col>
              <Col xs={12} className="ps-0" style={{ height: 32 }}>
                <SXDropdown
                  name="service_class"
                  id="service-class-select"
                  list={serviceClassOptions}
                  val={serviceClass}
                  dropDirection="down"
                  onUpdate={(value) => {
                    if (!value) {
                      return;
                    }
                    updateElementTimberSettings((timber) => {
                      timber.serviceclass = value.id;
                    });
                    // Service class affects kdef/kcr; recompute via the eurocode gamma endpoint.
                    dispatch(updateGammaApi());
                  }}
                  notSelectedName="Select service class"
                  localisation={false}
                  rawNames={true}
                  lang=""
                />
              </Col>
            </Row>
          </Col>

          <Col xs={12} md={6} lg={3}>
            <Row className="container-row">
              <Col className="editfield-label">
                <span>
                  <Trans i18nKey="kdef_.label" defaults="k<sub>def</sub>" components={{ sub: <sub /> }} />
                </span>
              </Col>
              <Col xs={12} className="ps-0" style={{ height: 32 }}>
                <SXEditField
                  type="number"
                  val={kdef}
                  textAlign="left"
                  fullWidth
                  disabled
                  onUpdate={(value) => {
                    const nextKdef = Number(value);
                    updateElementTimberSettings((timber) => {
                      timber.kdef = Number.isFinite(nextKdef) ? nextKdef : 0;
                    });
                  }}
                />
              </Col>
            </Row>
          </Col>

          <Col xs={12} md={6} lg={3}>
            <Row className="container-row">
              <Col className="editfield-label">
                <span>
                  <Trans i18nKey="ksys_.label" defaults="k<sub>sys</sub>" components={{ sub: <sub /> }} />
                </span>
              </Col>
              <Col xs={12} className="ps-0" style={{ height: 32 }}>
                <SXEditField
                  type="number"
                  val={ksys}
                  textAlign="left"
                  fullWidth
                  onUpdate={(value) => {
                    const nextKsys = Number(value);
                    updateElementTimberSettings((_timber, timberBeam) => {
                      timberBeam.ksys = Number.isFinite(nextKsys) ? nextKsys : 0;
                    });
                  }}
                />
              </Col>
            </Row>
          </Col>

          <Col xs={12} md={6} lg={3}>
            <Row className="container-row">
              <Col className="editfield-label">
                <span>
                  <Trans i18nKey="kcr_.label" defaults="k<sub>cr</sub>" components={{ sub: <sub /> }} />
                </span>
              </Col>
              <Col xs={12} className="ps-0" style={{ height: 32 }}>
                <SXEditField
                  type="number"
                  val={kcr}
                  textAlign="left"
                  fullWidth
                  disabled
                  onUpdate={(value) => {
                    const nextKcr = Number(value);
                    updateElementTimberSettings((timber) => {
                      timber.kcr = Number.isFinite(nextKcr) ? nextKcr : 0;
                    });
                  }}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      )}

      <MaterialSettingsModal
        show={showSettingsModal}
        close={() => setShowSettingsModal(false)}
        selectedGroup={selectedGroup}
      />
    </Container>
  );
};
